import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";
import { Share2, Loader2, Globe, Heart, ExternalLink } from "lucide-react";

export const ShareToCommunity = ({ analysisId, userPlan, onShared, t: tProp }) => {
  const { t: tCtx } = useI18n();
  const t = tProp || tCtx;
  const canShare = userPlan === "TRADER" || userPlan === "PRO" || userPlan === "OWNER";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ is_public: false, share_likes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!analysisId) return;
    (async () => {
      try {
        const { data } = await api.get(`/analyses/${analysisId}/share-status`);
        setStatus({
          is_public: !!data.is_public,
          share_likes: data.share_likes || 0,
        });
      } catch {
        /* ignore - probably not owner */
      } finally {
        setLoading(false);
      }
    })();
  }, [analysisId]);

  const handleShare = async () => {
    if (!canShare) {
      toast.error(t("share_requires_upgrade"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/analyses/${analysisId}/share`, {
        is_public: !status.is_public,
      });
      setStatus((s) => ({ ...s, is_public: data.is_public }));
      toast.success(data.is_public ? t("share_success") : t("share_unshare_success"));
      setOpen(false);
      onShared?.(data.is_public);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "SHARE_REQUIRES_UPGRADE") {
        toast.error(t("share_requires_upgrade"));
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to update sharing");
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className="h-9 px-3 inline-flex items-center font-mono text-xs text-neutral-500 border border-white/10"
        data-testid="share-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </div>
    );
  }

  // Already shared — show indicator + Unshare option in dialog
  if (status.is_public) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 h-9 border border-bull/40 bg-bull/[0.06] text-bull font-mono text-xs hover:bg-bull/[0.10] transition-colors"
          data-testid="share-public-indicator"
        >
          <Globe className="h-3.5 w-3.5" strokeWidth={2} />
          {t("share_public_label")}
          {status.share_likes > 0 && (
            <span className="inline-flex items-center gap-0.5 ml-1 text-bear">
              <Heart className="h-3 w-3" strokeWidth={2} fill="currentColor" />
              {status.share_likes}
            </span>
          )}
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="bg-[#0a0a0a] border-white/10 rounded-none max-w-md"
            data-testid="share-dialog"
          >
            <DialogTitle className="text-xl font-semibold tracking-tight">
              {t("share_public_label")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 text-sm mt-2">
              {t("share_dialog_desc")}
            </DialogDescription>
            <div className="mt-4 surface-card p-4 flex items-center justify-between">
              <span className="font-mono text-xs text-neutral-400">Likes</span>
              <span className="font-mono text-sm text-bear inline-flex items-center gap-1">
                <Heart className="h-3.5 w-3.5" strokeWidth={2} fill="currentColor" />
                {status.share_likes}
              </span>
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <Button
                onClick={() => window.open(`/community/${analysisId}`, "_blank", "noopener,noreferrer")}
                variant="outline"
                className="rounded-none border-white/15 bg-transparent hover:bg-bull/5 hover:border-bull/40 hover:text-bull h-10"
                data-testid="share-view-public-btn"
              >
                <ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {t("community_view_full")}
              </Button>
              <Button
                onClick={handleShare}
                disabled={busy}
                variant="outline"
                className="rounded-none border-bear/30 bg-transparent text-bear hover:bg-bear/5 hover:border-bear/50 h-10"
                data-testid="share-unshare-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("share_remove")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Not shared — show Share button (disabled if FREE, but click prompts upgrade)
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className={`rounded-none border-white/15 bg-transparent h-9 ${
          canShare
            ? "hover:bg-bull/5 hover:border-bull/40 hover:text-bull"
            : "text-neutral-500 hover:border-warn/40 hover:text-warn"
        }`}
        onClick={() => (canShare ? setOpen(true) : toast.error(t("share_requires_upgrade")))}
        data-testid="share-btn"
      >
        <Share2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
        {t("share_btn")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[#0a0a0a] border-white/10 rounded-none max-w-md"
          data-testid="share-dialog"
        >
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {t("share_dialog_title")}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm mt-2">
            {t("share_dialog_desc")}
          </DialogDescription>
          <div className="flex gap-2 pt-5">
            <Button
              onClick={handleShare}
              disabled={busy}
              className="flex-1 rounded-none bg-bull text-black hover:bg-bull/90 h-10 font-medium"
              data-testid="share-confirm-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("share_confirm")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="rounded-none"
              data-testid="share-cancel-btn"
            >
              {t("share_cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
